import type { CaseStage, MoneyView, OfferAvailability, OfferOptionalTerm, OfferView } from '@tex/finance-shared'
import { type ReactNode, useState } from 'react'
import { errorMessage } from '../lib/api'
import { formatAmount, formatDate, formatDay } from '../lib/format'
import { DISCLOSURE_STATUS_LABELS, OFFER_STATUS_LABELS, PAYMENT_FREQUENCY_LABELS, statedRate, useOffers, useSelectOffer } from '../lib/offers'
import { Alert, Button, Card, Field, Mono, Row, Textarea } from './ui'

/** Second-person copy for the buyer; the server message is written for the dealer/Tex audience. */
const BORROWER_AVAILABILITY_MESSAGES: Partial<Record<OfferAvailability, string>> = {
  offer_available: 'An offer is available for you to review.',
  offer_selected: 'You have selected an offer.',
}

export function OffersCard({ caseId, audience, stage }: { caseId: string; audience: 'borrower' | 'dealer' | 'tex'; stage: CaseStage }) {
  const offers = useOffers(caseId)
  if (offers.isPending) return <p className="text-sm text-neutral-500">Loading offers…</p>
  if (offers.isError) return <Alert tone="error">{errorMessage(offers.error)}</Alert>
  const data = offers.data

  if (data.audience === 'dealer') {
    return (
      <Card title="Financing offers">
        <p className="text-sm">{data.availability.message}</p>
        <p className="mt-2 text-xs text-neutral-500">
          {data.availability.submissions_awaiting} partner response{data.availability.submissions_awaiting === 1 ? '' : 's'} pending ·{' '}
          {data.availability.offers_active} open offer{data.availability.offers_active === 1 ? '' : 's'}. Offer terms and partner names are between the buyer and Tex.
        </p>
      </Card>
    )
  }

  if (data.audience !== audience) return <Alert tone="error">This view is only rendered from the {audience} projection.</Alert>

  const canSelect = stage === 'offer_review'
  const canBackOut = audience === 'tex' && stage === 'closing'
  return (
    <Card title={audience === 'borrower' ? 'Your financing offers' : 'Offers'}>
      <p className="text-sm">
        {(audience === 'borrower' && BORROWER_AVAILABILITY_MESSAGES[data.availability.availability]) || data.availability.message}
      </p>
      {audience === 'borrower' && data.offers.length > 0 && (
        <p className="mt-1 text-xs text-neutral-500">
          Choosing an offer tells Tex which terms you prefer. It is not a signed agreement and does not move any money; the partner's paperwork
          follows in closing.
        </p>
      )}
      {audience === 'tex' && data.offers.length > 0 && stage === 'offer_review' && (
        <p className="mt-1 text-xs text-neutral-500">
          Record a selection only on the buyer's instruction and say how it reached you. Offers withheld from the buyer are marked below.
        </p>
      )}
      {!canSelect && data.offers.length > 0 && stage !== 'closed' && (
        <p className="mt-1 text-xs text-neutral-500">
          {canBackOut
            ? 'The buyer cannot change their selection in closing. If the deal changed and the buyer asks to stop, clear the selection on their instruction: closing ends and the file returns to offer review.'
            : stage === 'closing' || stage === 'funded'
              ? 'The selection is locked while the case is in closing or funded.'
              : 'Selection opens once the file is in offer review.'}
        </p>
      )}
      {data.offers.length > 0 && (
        <ul className="mt-4 space-y-4">
          {data.offers.map((o) => (
            <OfferItem key={o.id} caseId={caseId} offer={o} audience={audience} caseVersion={data.case_version} canSelect={canSelect} canBackOut={canBackOut} />
          ))}
        </ul>
      )}
    </Card>
  )
}

function OfferItem({
  caseId,
  offer,
  audience,
  caseVersion,
  canSelect,
  canBackOut,
}: {
  caseId: string
  offer: OfferView
  audience: 'borrower' | 'tex'
  caseVersion: number
  canSelect: boolean
  canBackOut: boolean
}) {
  const select = useSelectOffer(caseId)
  const [confirming, setConfirming] = useState<'select' | 'clear' | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const t = offer.terms
  const selectable = canSelect && offer.status === 'active' && offer.disclosure.presentable_to_buyer
  const noteRequired = audience === 'tex'

  const run = async (offerId: string | null) => {
    setError(null)
    try {
      await select.mutateAsync({ expected_version: caseVersion, offer_id: offerId, ...(note.trim() ? { note: note.trim() } : {}) })
      setConfirming(null)
      setNote('')
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <li className={`rounded-lg border p-4 ${offer.selected ? 'border-tex-blue bg-blue-50/40' : 'border-[var(--border-subtle)]'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-medium">{offer.partner_display_name}</span>
          <span className="text-neutral-500"> · {t.product_type}</span>
          {offer.outcome === 'conditional_offer' && <span className="text-neutral-500"> · conditional</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {offer.selected && <Tag tone="blue">Selected</Tag>}
          <Tag tone={offer.status === 'active' ? 'green' : 'grey'}>{OFFER_STATUS_LABELS[offer.status]}</Tag>
          {offer.synthetic && <Tag tone="grey">Synthetic</Tag>}
          {audience === 'tex' && !offer.disclosure.presentable_to_buyer && <Tag tone="amber">Withheld from buyer</Tag>}
        </div>
      </div>

      <dl className="mt-3 grid gap-x-6 sm:grid-cols-2">
        <Row label="Approved amount" value={<Mono className="font-semibold">{formatAmount(t.approved_amount)}</Mono>} />
        <Row label="Term" value={`${t.term_months} months, paid ${PAYMENT_FREQUENCY_LABELS[t.payment_frequency]}`} />
        <Row label="Payment" value={money(t.payment_amount, t.unstated_terms, 'payment_amount')} />
        <Row label="Rate" value={statedRate(t) ?? unstated(t.unstated_terms, 'rate')} />
        <Row label="Down payment" value={money(t.down_payment, t.unstated_terms, 'down_payment')} />
        <Row label="Fees" value={money(t.fees, t.unstated_terms, 'fees')} />
        <Row label="Residual / buyout" value={money(t.residual, t.unstated_terms, 'residual')} />
        <Row label="Total finance charge" value={money(t.total_finance_charge, t.unstated_terms, 'total_finance_charge')} />
        <Row label="Expires" value={t.expires_at ? formatDay(t.expires_at) : unstated(t.unstated_terms, 'expires_at')} />
        <Row label="Disclosure" value={DISCLOSURE_STATUS_LABELS[offer.disclosure.status]} />
      </dl>

      {(t.guarantee_requirements.length > 0 || t.prepayment_terms || t.buyout_terms) && (
        <dl className="mt-2 text-sm">
          {t.guarantee_requirements.length > 0 && <Row label="Guarantees" value={t.guarantee_requirements.join('; ')} />}
          {t.prepayment_terms && <Row label="Prepayment" value={t.prepayment_terms} />}
          {t.buyout_terms && <Row label="Buyout" value={t.buyout_terms} />}
        </dl>
      )}

      {t.conditions.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Partner conditions</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
            {t.conditions.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-3 text-xs text-neutral-400">
        Terms as stated by the partner, recorded {formatDate(offer.recorded_at)}. Tex does not compute rates or charges the partner did not state.
      </p>

      {((canSelect && (selectable || offer.selected)) || (canBackOut && offer.selected)) && (
        <div className="mt-3 space-y-2">
          {confirming === null ? (
            <div className="flex flex-wrap gap-2">
              {canSelect && selectable && !offer.selected && (
                <Button type="button" onClick={() => setConfirming('select')}>
                  {audience === 'borrower' ? 'Choose this offer' : 'Record buyer selection'}
                </Button>
              )}
              {offer.selected && (
                <Button type="button" variant="secondary" onClick={() => setConfirming('clear')}>
                  {audience === 'borrower' ? 'Clear my choice' : canBackOut ? 'Clear selection and stop closing' : 'Clear selection'}
                </Button>
              )}
            </div>
          ) : (
            <form
              className="space-y-2 rounded-md bg-neutral-50 p-3"
              onSubmit={(e) => {
                e.preventDefault()
                void run(confirming === 'select' ? offer.id : null)
              }}
            >
              <p className="text-sm">
                {confirming === 'select'
                  ? audience === 'borrower'
                    ? `Tell Tex you prefer the ${offer.partner_display_name} ${t.product_type}? You can change this until closing starts.`
                    : `Record that the buyer selected the ${offer.partner_display_name} ${t.product_type}?`
                  : canBackOut
                    ? 'Clear the selection on the buyer\'s instruction? Closing ends and the file returns to offer review; the offer itself is kept.'
                    : 'Clear the current selection?'}
              </p>
              <Field label={noteRequired ? 'How the instruction reached Tex' : 'Note (optional)'}>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required={noteRequired} minLength={noteRequired ? 3 : undefined} />
              </Field>
              {error && <Alert tone="error">{error}</Alert>}
              <div className="flex gap-2">
                <Button type="submit" disabled={select.isPending}>
                  Confirm
                </Button>
                <Button type="button" variant="ghost" onClick={() => setConfirming(null)} disabled={select.isPending}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </li>
  )
}

function money(m: MoneyView | null, unstatedTerms: OfferOptionalTerm[], term: OfferOptionalTerm): ReactNode {
  if (m) return <Mono>{formatAmount(m)}</Mono>
  return unstated(unstatedTerms, term)
}

/** A term the partner did not state is shown as exactly that — never as zero. */
function unstated(unstatedTerms: OfferOptionalTerm[], term: OfferOptionalTerm): ReactNode {
  return <span className="text-neutral-400">{unstatedTerms.includes(term) ? 'Not stated by partner' : '—'}</span>
}

export function Tag({ tone, children }: { tone: 'blue' | 'green' | 'grey' | 'amber' | 'red'; children: ReactNode }) {
  const tones = {
    blue: 'bg-blue-50 text-tex-blue border-blue-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    grey: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>
}
